///
/// EarthBrowser
///
/// Created by Matt Giger on 11/18/2023
/// Copyright (c) 2023 Geodata Labs
///

import { cube } from "@stovokor/math/cube"
import { type sphere } from "@stovokor/math/sphere"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

export class Cube implements Geometry {
	public position: VertexAttribute
	public normal: VertexAttribute
	public texCoord: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public available: boolean = true
	public bounds: cube
	public boundingSphere: sphere

	constructor(context: StovokorContext) {
		this.position = new VertexAttribute(context, 3)
		this.normal = new VertexAttribute(context, 3)
		this.texCoord = new VertexAttribute(context, 2)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position, this.normal, this.texCoord])

		// front
		this.position.addPos(0.5, -0.5, 0.5)
		this.texCoord.add(1, 0)
		this.normal.add(0, 0, 1)
		this.position.addPos(-0.5, -0.5,  0.5)
		this.texCoord.add(0, 0)
		this.normal.add(0, 0, 1)
		this.position.addPos(-0.5,  0.5,  0.5)
		this.texCoord.add(0, 1)
		this.normal.add(0, 0, 1)
		this.position.addPos(0.5,  0.5,  0.5)
		this.texCoord.add(1, 1)
		this.normal.add(0, 0, 1)
		this.indexBuffer.quad(2, 1, 0, 3)

		// back
		this.position.addPos(0.5, -0.5, -0.5)
		this.texCoord.add(0, 0)
		this.normal.add(0, 0, -1)
		this.position.addPos(-0.5, -0.5, -0.5)
		this.texCoord.add(1, 0)
		this.normal.add(0, 0, -1)
		this.position.addPos(-0.5,  0.5, -0.5)
		this.texCoord.add(1, 1)
		this.normal.add(0, 0, -1)
		this.position.addPos(0.5,  0.5, -0.5)
		this.texCoord.add(0, 1)
		this.normal.add(0, 0, -1)
		this.indexBuffer.quad(4, 5, 6, 7)

		// top
		this.position.addPos(0.5,  0.5, -0.5)
		this.texCoord.add(1, 1)
		this.normal.add(0, 1, 0)
		this.position.addPos(-0.5,  0.5, -0.5)
		this.texCoord.add(0, 1)
		this.normal.add(0, 1, 0)
		this.position.addPos(-0.5,  0.5,  0.5)
		this.texCoord.add(0, 0)
		this.normal.add(0, 1, 0)
		this.position.addPos(0.5,  0.5,  0.5)
		this.texCoord.add(1, 0)
		this.normal.add(0, 1, 0)
		this.indexBuffer.quad(8, 9, 10, 11)

		// bottom
		this.position.addPos(-0.5, -0.5, -0.5)
		this.texCoord.add(0, 1)
		this.normal.add(0, -1, 0)
		this.position.addPos(0.5, -0.5, -0.5)
		this.texCoord.add(1, 1)
		this.normal.add(0, -1, 0)
		this.position.addPos(0.5, -0.5,  0.5)
		this.texCoord.add(1, 0)
		this.normal.add(0, -1, 0)
		this.position.addPos(-0.5, -0.5,  0.5)
		this.texCoord.add(0, 0)
		this.normal.add(0, -1, 0)
		this.indexBuffer.quad(12, 13, 14, 15)

		// left
		this.position.addPos(-0.5,  0.5, -0.5)
		this.texCoord.add(1, 1)
		this.normal.add(-1, 0, 0)
		this.position.addPos(-0.5, -0.5, -0.5)
		this.texCoord.add(1, 0)
		this.normal.add(-1, 0, 0)
		this.position.addPos(-0.5, -0.5,  0.5)
		this.texCoord.add(0, 0)
		this.normal.add(-1, 0, 0)
		this.position.addPos(-0.5,  0.5, 0.5)
		this.texCoord.add(0, 1)
		this.normal.add(-1, 0, 0)
		this.indexBuffer.quad(16, 17, 18, 19)

		// right
		this.position.addPos(0.5, -0.5, -0.5)
		this.texCoord.add(0, 0)
		this.normal.add(1, 0, 0)
		this.position.addPos(0.5,  0.5, -0.5)
		this.texCoord.add(0, 1)
		this.normal.add(1, 0, 0)
		this.position.addPos(0.5,  0.5,  0.5)
		this.texCoord.add(1, 1)
		this.normal.add(1, 0, 0)
		this.position.addPos(0.5, -0.5, 0.5)
		this.texCoord.add(1, 0)
		this.normal.add(1, 0, 0)
		this.indexBuffer.quad(20, 21, 22, 23)

		this.bounds = new cube(-0.5, 0.5)
		this.boundingSphere = this.bounds.sphere
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}